import api from './api';

export interface CpcSuggestion {
  code: string;
  label: string;
  section: string;
  section_label: string;
  class_code: string;
  class_label: string;
  subclass_code: string;
  subclass_label: string;
}

export interface CpcSubclassSuggestion {
  code: string;
  title: string;
  section: string;
  section_title: string;
  class_code: string;
  class_title: string;
}

export interface OpenAlexSubfieldSuggestion {
  id: string;
  display_name: string;
  field_id: string;
  field_name: string;
  domain_id: string;
  domain_name: string;
}

export interface OpenAlexTopicSuggestion {
  id: string;
  display_name: string;
  subfield_id: string;
  subfield_name: string;
  field_id: string;
  field_name: string;
  domain_id: string;
  domain_name: string;
}

export interface StateOfTheArt {
  content: string;
  version: number;
  generation_status: 'empty' | 'scoping' | 'draft' | 'enriched' | 'finalized';
  last_modified_date: string | null;
  last_modified_by: string | null;
}

export interface FtoPatentRef {
  patent_id: string;
  country: string;
  family_root_id: string;
  kind_code?: string | null;
  included?: boolean;
  notes?: string | null;
  claims_snapshot?: string | null;
  claims_version_id?: string | null;
}

export interface FtoFinalPatentEntry {
  patent_id: string;
  country: string;
  family_root_id: string;
  kind_code?: string | null;
  legal_status?: string | null;
  expiry_date?: string | null;
  designated_states?: string[] | null;
  claims_status?: string | null;
  claims_kind_code?: string | null;
  claims_source?: string | null;
  claims_date?: string | null;
  document_id?: string | null;
  has_claims_snapshot?: boolean;
}

export interface PriorArtRelevanceEntry {
  document_id: string;
  relevance_score: number;
  relevance_rationale: string;
  is_included: boolean;
}

export interface RefinedFeature {
  feature_id: string;
  parent_id?: string | null;
  relation: 'conjunctive' | 'alternative' | 'optional';
  text: string;
  construed_meaning: string;
  construction_evidence: string;
  feature_class: string;
  is_optional: boolean;
  alternative_group_id?: string | null;
}

export interface SolutionClaim {
  id: string;
  claim_number: number;
  category: 'product' | 'method' | 'programme' | 'storage_medium' | 'computer' | 'use';
  is_independent: boolean;
  depends_on: number[];
  claimable_item_id?: string | null;
  claim_text: string;
  features: RefinedFeature[];
  claim_structure: { preamble: string; two_part_form: boolean; has_alternatives: boolean; has_optional_features: boolean };
  decomposition_source_hash?: string | null;
  decomposition_is_stale?: boolean;
  decomposition_model_id?: string | null;
  decomposition_at?: string | null;
  source: 'llm_generated' | 'user_created' | 'user_edited';
  created_at: string;
  last_modified_at: string;
  last_modified_by?: string | null;
}

export interface Solution {
  id: string;
  name: string;
  version: number;
  solution_description?: string;
  invention_summary?: string;
  claims?: string;
  fto_analysis?: string;
  fto_countries: string[];
  fto_initial_patents: string[];
  fto_patent_refs: FtoPatentRef[];
  fto_final_patents: FtoFinalPatentEntry[];
  industrialization?: string;
  patentability?: string;
  patent_application?: string;
  context_kbs: Record<string, string | null>;
  created_at: string;
  last_modified_at: string;
  last_modified_by?: string | null;
  claims_list?: string[];
  claims_structured?: SolutionClaim[];
  priority_target_date?: string | null;
  patentability_documents?: PriorArtRelevanceEntry[];
  description_html?: string;
}

export interface SubProject {
  id: string;
  name: string;
  problem_statement?: string;
  status?: string;
  state_of_the_art?: StateOfTheArt | null;
  context_kbs?: Record<string, string>;
  solutions?: Solution[];
  project_id: string;
  kb_id?: string | null;
  kb_ids?: string[];
  features_list?: {
    features: Array<{
      id: string;
      number: number;
      feature_name: string;
      feature_description: string;
      keywords: string;
      included_in_search: boolean;
      source: string;
      created_at: string;
      last_modified_at: string;
    }>;
    version: number;
    last_saved_at?: string;
  };
  created_date: string;
  last_updated: string;
}

export interface CreateSubProjectRequest {
  name: string;
  problem_statement?: string;
  project_id: string;
}

export interface UpdateSubProjectRequest {
  name?: string;
  problem_statement?: string;
  status?: string;
  context_kbs?: Record<string, string>;
  state_of_the_art?: StateOfTheArt | null;
}

export const subProjectService = {
  async getAll(): Promise<SubProject[]> {
    const response = await api.get<SubProject[]>('/sub-projects/');
    return response.data;
  },

  async getByProjectId(projectId: string): Promise<SubProject[]> {
    const response = await api.get<SubProject[]>(`/sub-projects/project/${projectId}`);
    return response.data;
  },

  async getById(id: string): Promise<SubProject> {
    const response = await api.get<SubProject>(`/sub-projects/${id}`);
    return response.data;
  },

  async create(data: CreateSubProjectRequest): Promise<SubProject> {
    const response = await api.post<SubProject>('/sub-projects/', data);
    return response.data;
  },

  async update(id: string, data: UpdateSubProjectRequest): Promise<SubProject> {
    const response = await api.put<SubProject>(`/sub-projects/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/sub-projects/${id}`);
  },

  // Multi-KB Management (Phase 3)
  async listKnowledgeBases(subProjectId: string): Promise<unknown[]> {
    const response = await api.get<unknown[]>(`/sub-projects/${subProjectId}/knowledge-bases`);
    return response.data;
  },

  async addKnowledgeBase(subProjectId: string, kbId: string, kbName?: string): Promise<SubProject> {
    const params = kbName ? `?kb_name=${encodeURIComponent(kbName)}` : '';
    const response = await api.post<SubProject>(`/sub-projects/${subProjectId}/knowledge-bases/${kbId}${params}`);
    return response.data;
  },

  async removeKnowledgeBase(subProjectId: string, kbId: string, deleteKb: boolean = false): Promise<SubProject> {
    const response = await api.delete<SubProject>(`/sub-projects/${subProjectId}/knowledge-bases/${kbId}?delete_kb=${deleteKb}`);
    return response.data;
  },

  async suggestCpc(subProjectId: string): Promise<CpcSubclassSuggestion[]> {
    const response = await api.post<{ status: string; subclasses: CpcSubclassSuggestion[]; phase: string }>(
      `/sub-projects/${subProjectId}/suggest-cpc`
    );
    return response.data.subclasses;
  },

  async refineCpcSuggestions(subProjectId: string, subclassCodes: string[]): Promise<CpcSuggestion[]> {
    const response = await api.post<{ status: string; suggestions: CpcSuggestion[]; phase: string }>(
      `/sub-projects/${subProjectId}/suggest-cpc/refine`,
      { subclass_codes: subclassCodes }
    );
    return response.data.suggestions;
  },

  async suggestTopics(subProjectId: string): Promise<OpenAlexSubfieldSuggestion[]> {
    const response = await api.post<{ status: string; subfields: OpenAlexSubfieldSuggestion[]; phase: string }>(
      `/sub-projects/${subProjectId}/suggest-topics`
    );
    return response.data.subfields;
  },

  async refineTopics(subProjectId: string, subfieldIds: string[]): Promise<OpenAlexTopicSuggestion[]> {
    const response = await api.post<{ status: string; suggestions: OpenAlexTopicSuggestion[]; phase: string }>(
      `/sub-projects/${subProjectId}/suggest-topics/refine`,
      { subfield_ids: subfieldIds }
    );
    return response.data.suggestions;
  },
};
